<template>
  <div>
    <v-tabs
      v-model="selectedShift"
      class="mt-4"
      background-color="transparent"
      color="primary"
      slider-color="primary"
      :disabled="loading.addNewMeal || loading.fetchingMenus"
    >
      <v-tab v-for="(shift, key) in shiftTabs" :key="key" class="text-capitalize">
        {{ shift.label }}
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="selectedShift" class="mt-4">
      <v-tab-item v-for="(shift, shiftIndex) in shiftTabs" :key="shiftIndex">
        <div class="meal-cards-container">
          <v-overlay v-if="loading.addNewMeal || loading.fetchingMenus" :absolute="true">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
          </v-overlay>

          <v-row v-if="!loading.addNewMeal && !loading.fetchingMenus">
            <v-col
              v-for="(meal, index) in filteredMeals"
              :key="meal.id"
              cols="12"
              sm="6"
              md="4"
              lg="4"
            >
              <meal-order-card
                :meal="meal"
                :shift-key="currentShiftKey"
                :disabled="loading.saveMealQuantity"
                @quantity-changed="onQuantityChanged"
              />
            </v-col>
            <v-col v-if="filteredMeals.length === 0" cols="12">
              <v-card class="empty-state-card" flat outlined>
                <v-card-text class="text-center py-12">
                  <v-avatar color="grey lighten-3" size="80" class="mb-4">
                    <v-icon size="48" color="grey">mdi-food-off</v-icon>
                  </v-avatar>
                  <div class="text-h6 font-weight-bold grey--text text--darken-2 mb-2">
                    {{ $t("no_meals_available") }}
                  </div>
                  <div class="text-body-2 grey--text text--darken-1 mb-1">
                    {{
                      $t("no_meals_for_shift_and_date", {
                        shift: shiftTabs[selectedShift].label.toLowerCase(),
                        date: localeDate(selectedDate),
                      })
                    }}
                  </div>
                  <div class="text-body-2 grey--text mb-6">
                    {{ $t("add_meals_to_menu_first") }}
                  </div>
                  <primary-button :to="{ name: 'jelovnik' }" icon="mdi-food-variant">
                    {{ $t("go_to_menu") }}
                  </primary-button>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import reportService from "@/services/report";
import MealOrderCard from "../components/MealOrderCard.vue";
import menuService from "@/services/menu";

export default {
  name: "AdditionalMealsShiftTabs",
  components: {
    MealOrderCard,
  },
  mixins: [hasSnackbar],
  props: {
    company: {
      type: Object,
      required: true,
    },
    selectedDate: {
      type: String,
      required: true,
    },
    search: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      newMealAllData: [],
      selectedShift: 0,
      loading: {
        addNewMeal: false,
        saveMealQuantity: false,
        fetchingMenus: false,
        fetchingMealDetails: false,
      },
    };
  },

  computed: {
    shiftTabs() {
      return [
        { label: this.$t("1st_shift"), key: "shift_one", id: 1 },
        { label: this.$t("2nd_shift"), key: "shift_two", id: 2 },
        { label: this.$t("3rd_shift"), key: "shift_three", id: 3 },
        { label: this.$t("4th_shift"), key: "shift_four", id: 4 },
      ];
    },

    currentShiftKey() {
      return this.shiftTabs[this.selectedShift]?.key || "shift_one";
    },

    currentShiftId() {
      return this.shiftTabs[this.selectedShift]?.id || 1;
    },

    filteredMeals() {
      if (
        !this.newMealAllData.length ||
        this.loading.addNewMeal ||
        this.loading.fetchingMenus
      )
        return [];

      const meals = this.newMealAllData.filter((meal) => !this.isTotalRow(meal));

      // Filter by current shift availability - show only meals available for current shift
      const shiftFilteredMeals = meals
        .filter((meal) => {
          // Check if meal is available for current shift (1 = available, 0 = not available)
          return meal[this.currentShiftKey] === 1;
        })
        .filter((meal) => {
          return meal.groups.some((group) => {
            return group.menu_type?.name === this.company.type;
          });
        });

      if (!this.search) return shiftFilteredMeals;

      return shiftFilteredMeals.filter(
        (meal) =>
          meal.name.toLowerCase().includes(this.search.toLowerCase()) ||
          meal.id.toString().includes(this.search)
      );
    },
  },

  methods: {
    onQuantityChanged({ meal, shiftKey, quantity }) {
      // Update the meal quantity
      meal.orders[shiftKey] = quantity;
      this.updateMealQuantity(meal);
    },

    async updateMealQuantity(meal) {
      // Recalculate total for this meal
      meal.orders.total = this.calculateItemTotal(meal.orders);

      // Recalculate grand total
      this.recalculateGrandTotal();

      // Save the change immediately
      await this.saveMealQuantity(meal, this.currentShiftKey, this.currentShiftId);
    },

    async saveMealQuantity(meal, shiftKey, shiftId) {
      if (this.loading.saveMealQuantity) {
        console.warn("Save operation already in progress, skipping duplicate request");
        return;
      }

      if (!this.company?.id) {
        console.error("Company ID is missing for saving additional meal.");
        return;
      }

      this.loading.saveMealQuantity = true;

      const payload = {
        company_id: this.company.id,
        user_id: this.$store.state.user.user.id,
        shift: `${shiftId}-smena`,
        quantity: meal.orders[shiftKey],
        meal: {
          id: meal.id,
          restaurant_id: meal.restaurant_id,
          date: this.selectedDate,
        },
      };

      try {
        const { data } = await reportService.addAdditionalMeal(payload);
        if (data) {
          this.$emit("save");
        }
      } catch (error) {
        console.error("Error adding additional meal:", error);
        this.showErrorSnackbar(this.$t("failed_to_save_changes"));
        await this.addNewMeal();
      } finally {
        this.loading.saveMealQuantity = false;
      }
    },

    async addNewMeal() {
      if (this.loading.addNewMeal) {
        console.warn(
          "Add new meal operation already in progress, skipping duplicate request"
        );
        return;
      }

      if (!this.company?.id) {
        console.warn("Company data is not available for adding a new meal.");
        this.newMealAllData = [];
        return;
      }

      this.loading.addNewMeal = true;
      this.loading.fetchingMenus = true;

      try {
        // Step 1: Get all menus for the current date
        const date = this.selectedDate;
        console.log(date);
        const menusResponse = await menuService.getWebAppOnlyMenu(date, date);

        this.loading.fetchingMenus = false;

        if (!menusResponse.data || !menusResponse.data.length) {
          this.newMealAllData = [];
          return;
        }

        // Step 2: Filter menus for current date and get all meals
        const todayMenus = menusResponse.data.filter((menu) => menu.date === date);

        if (!todayMenus.length) {
          this.newMealAllData = [];
          return;
        }

        // Step 3: Fetch detailed menu data with meals for each menu
        this.loading.fetchingMealDetails = true;
        const allMeals = [];

        for (const menu of todayMenus) {
          try {
            const menuDetailResponse = await menuService.getMenuById(menu.id, true);

            if (menuDetailResponse.data && menuDetailResponse.data.meals) {
              // Process meals from this menu
              const menuMeals = menuDetailResponse.data.meals.map((meal) => ({
                ...meal,
                menu_id: menu.id,
                // These fields indicate availability for each shift (1 = available, 0 = not available)
                shift_one: meal.shift_one || 0,
                shift_two: meal.shift_two || 0,
                shift_three: meal.shift_three || 0,
                shift_four: meal.shift_four || 0,
                // Orders represent quantities for each shift (initialized to 0)
                orders: {
                  shift_one: 0,
                  shift_two: 0,
                  shift_three: 0,
                  shift_four: 0,
                  total: 0,
                },
              }));

              allMeals.push(...menuMeals);
            }
          } catch (error) {
            console.error(`Error fetching menu ${menu.id}:`, error);
          }
        }

        this.loading.fetchingMealDetails = false;

        if (allMeals.length) {
          // Remove duplicates based on meal ID
          const uniqueMeals = allMeals.filter(
            (meal, index, self) => index === self.findIndex((m) => m.id === meal.id)
          );

          // Apply restaurant origin quantities from company data
          this.applyRestaurantOriginQuantities(uniqueMeals);

          const totalRow = this.createTotalRow(uniqueMeals);
          this.newMealAllData = [...uniqueMeals, totalRow];
        } else {
          this.newMealAllData = [];
        }
      } catch (error) {
        console.error("Error fetching menus and meals:", error);
        this.showErrorSnackbar(this.$t("failed_to_load_menu"));
        this.newMealAllData = [];
      } finally {
        this.loading.addNewMeal = false;
        this.loading.fetchingMenus = false;
        this.loading.fetchingMealDetails = false;
      }
    },

    calculateItemTotal(orders) {
      return this.shiftTabs.reduce((total, shift) => total + (orders[shift.key] || 0), 0);
    },

    createTotalRow(data) {
      const totals = {
        shift_one: 0,
        shift_two: 0,
        shift_three: 0,
        shift_four: 0,
        total: 0,
      };

      data.forEach((item) => {
        this.shiftTabs.forEach((shift) => {
          totals[shift.key] += item.orders[shift.key] || 0;
        });
        totals.total += item.orders.total || 0;
      });

      return {
        id: "total",
        name: this.$t("totalU"),
        orders: totals,
      };
    },

    applyRestaurantOriginQuantities(meals) {
      // Check if company has meals data with restaurant origin
      if (!this.company?.meals || !Array.isArray(this.company.meals)) {
        return;
      }

      // Map company meals by ID for quick lookup
      const companyMealsMap = new Map();
      this.company.meals.forEach((companyMeal) => {
        companyMealsMap.set(companyMeal.id, companyMeal);
      });

      // Apply restaurant origin quantities to matching meals
      meals.forEach((meal) => {
        const companyMeal = companyMealsMap.get(meal.id);
        if (!companyMeal) return;

        // Check each shift for restaurant quantities and apply them
        const shiftKeys = ["shift_one", "shift_two", "shift_three", "shift_four"];

        shiftKeys.forEach((shiftKey) => {
          const companyShiftData = companyMeal[shiftKey];

          // If this shift exists and has restaurant quantity, set it
          if (
            companyShiftData &&
            typeof companyShiftData.restaurantQuantity === "number" &&
            companyShiftData.restaurantQuantity > 0
          ) {
            meal.orders[shiftKey] = companyShiftData.restaurantQuantity;
          }
        });

        // Recalculate total for this meal
        meal.orders.total = this.calculateItemTotal(meal.orders);
      });
    },

    recalculateGrandTotal() {
      if (!this.newMealAllData.length) return;

      const meals = this.newMealAllData.slice(0, -1); // Exclude total row
      const totals = {
        shift_one: 0,
        shift_two: 0,
        shift_three: 0,
        shift_four: 0,
        total: 0,
      };

      meals.forEach((meal) => {
        this.shiftTabs.forEach((shift) => {
          totals[shift.key] += meal.orders[shift.key] || 0;
        });
        totals.total += meal.orders.total || 0;
      });

      const totalRow = this.newMealAllData[this.newMealAllData.length - 1];
      if (totalRow && this.isTotalRow(totalRow)) {
        Object.keys(totals).forEach((key) => {
          this.$set(totalRow.orders, key, totals[key]);
        });
      }
    },

    isTotalRow(item) {
      return item?.name === this.$t("totalU");
    },

    resetData() {
      this.newMealAllData = [];
      this.selectedShift = 0;
      this.loading.addNewMeal = false;
      this.loading.saveMealQuantity = false;
      this.loading.fetchingMenus = false;
      this.loading.fetchingMealDetails = false;
    },
  },

  watch: {
    selectedDate(newVal, oldVal) {
      if (newVal !== oldVal && newVal) {
        this.addNewMeal();
      }
    },

    company: {
      handler(newVal, oldVal) {
        if (newVal?.id && (!oldVal || newVal.id !== oldVal.id)) {
          // Company changed, reload meals for current date
          this.addNewMeal();
        }
      },
      deep: true,
    },
  },

  mounted() {
    if (this.company?.id && this.selectedDate) {
      this.addNewMeal();
    }
  },
};
</script>

<style scoped>
.v-tabs-bar {
  background-color: transparent !important;
}

.v-tab {
  text-transform: none;
  font-weight: 500;
  letter-spacing: normal;
}

.v-tab.v-tab--active {
  background-color: rgba(25, 118, 210, 0.1);
  border-radius: 8px 8px 0 0;
}

.meal-cards-container {
  overflow: visible;
  padding: 16px 0;
  position: relative;
}

.empty-state-card {
  border-radius: 12px;
  border: 2px dashed #e0e0e0 !important;
  background-color: #fafafa;
}
</style>
